import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import AddIcon from '@mui/icons-material/Add'
import {
  computeDocument,
  format,
  isMutable,
  money,
  referenceAffichable,
  type CurrencyCode,
  type Transaction,
  type TransactionLineInput,
} from '@iobiz/shared'

/**
 * Correction d'une vente par l'ADMINISTRATEUR de la boutique (ADR-08 amendé).
 *
 * ⭐ Pourquoi cet écran existe : l'immuabilité tenait le commerçant en échec
 * sur un cas banal — une erreur de saisie constatée dans la minute, sur une
 * vente déjà validée. L'avoir est la bonne réponse comptable à un
 * remboursement, pas à un libellé faux ou à une quantité de trop ; il laisse
 * deux documents là où il fallait corriger un chiffre.
 *
 * ⚠️ Le MOTIF est exigé dès que le document n'est plus un brouillon, et le
 * serveur le redemande. Ce n'est pas une formalité : c'est la contrepartie qui
 * rend l'amendement défendable. L'état d'avant est archivé, le motif dit
 * pourquoi, et la facture reste reconstituable — ce que l'immuabilité
 * garantissait gratuitement.
 *
 * ⚠️ Les RÈGLEMENTS ne se touchent pas ici. Un encaissement est un fait, pas
 * une saisie : il se corrige par un encaissement inverse, visible. Modifier
 * les lignes peut donc laisser un reste dû ou un trop-perçu — c'est dit à
 * l'écran plutôt que masqué.
 */
export function ModifierVenteDialog({
  open,
  vente,
  devise,
  onClose,
  onValider,
}: {
  open: boolean
  vente: Transaction
  devise: CurrencyCode
  onClose: () => void
  onValider: (lignes: TransactionLineInput[], motif: string) => Promise<void> | void
}): JSX.Element {
  const [lignes, setLignes] = useState<TransactionLineInput[]>([])
  const [motif, setMotif] = useState('')
  const [enCours, setEnCours] = useState(false)

  // ⚠️ Repartir du document À CHAQUE ouverture : garder l'état entre deux
  // ouvertures ferait rouvrir la boîte sur des lignes déjà retouchées, sans
  // que rien ne le signale.
  useEffect(() => {
    if (!open) return
    setLignes(
      vente.lines.map((l) => ({
        ...(l.itemId ? { itemId: l.itemId } : {}),
        label: l.label,
        // ⚠️ zod 4 : un champ `.optional()` reste une clé du type inféré.
        description: l.description ?? undefined,
        quantity: l.quantity,
        unitPrice: l.unitPrice,
        discountRate: l.discountRate,
        taxRate: l.taxRate,
      })),
    )
    setMotif('')
  }, [open, vente])

  const brouillon = isMutable(vente.status)
  const prix = (v: number): string => format(money(v, devise), 'fr-FR')

  /**
   * ⭐ Les totaux sont recalculés par `computeDocument`, le MÊME calcul que le
   * serveur. Les recalculer à la main ici ferait diverger l'écran du document
   * enregistré, et l'écart n'apparaîtrait qu'à l'impression.
   */
  const totaux = computeDocument({
    currency: devise,
    lines: lignes.map((l) => ({
      quantity: Number(l.quantity) || 0,
      unitPrice: Number(l.unitPrice) || 0,
      discountRate: Number(l.discountRate) || 0,
      taxRate: Number(l.taxRate) || 0,
    })),
    globalDiscountRate: 0,
  })
  const nouveauTotal = totaux.totalAmount.amount
  const ecartReglement = vente.paidAmount - nouveauTotal

  const valide =
    lignes.length > 0 &&
    lignes.every((l) => String(l.label).trim().length > 0 && Number(l.quantity) > 0) &&
    (brouillon || motif.trim().length >= 3)

  function majLigne(index: number, champ: keyof TransactionLineInput, valeur: string): void {
    setLignes((p) =>
      p.map((l, i) =>
        i === index
          ? { ...l, [champ]: champ === 'label' ? valeur : (Number(valeur.replace(',', '.')) || 0) }
          : l,
      ),
    )
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Modifier {referenceAffichable(vente)}</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 4, pt: 4 }}>
        {!brouillon && (
          <Alert severity="warning">
            Ce document est validé. La modification sera <strong>archivée</strong> : l’état actuel
            est conservé, avec votre nom, la date et le motif.
          </Alert>
        )}

        {lignes.map((l, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              label="Désignation"
              value={l.label}
              onChange={(e) => majLigne(i, 'label', e.target.value)}
              sx={{ flex: 3 }}
              size="small"
            />
            <TextField
              label="Qté"
              value={l.quantity}
              onChange={(e) => majLigne(i, 'quantity', e.target.value)}
              sx={{ flex: 1 }}
              size="small"
              inputProps={{ inputMode: 'decimal' }}
            />
            <TextField
              label="Prix unitaire HT"
              value={l.unitPrice}
              onChange={(e) => majLigne(i, 'unitPrice', e.target.value)}
              sx={{ flex: 1.4 }}
              size="small"
              inputProps={{ inputMode: 'numeric' }}
            />
            <TextField
              label="TVA %"
              value={l.taxRate}
              onChange={(e) => majLigne(i, 'taxRate', e.target.value)}
              sx={{ flex: 1 }}
              size="small"
              inputProps={{ inputMode: 'decimal' }}
            />
            <IconButton
              aria-label="Retirer la ligne"
              onClick={() => setLignes((p) => p.filter((_, j) => j !== i))}
              sx={{ mt: 0.5 }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}

        <Box>
          <Button
            startIcon={<AddIcon />}
            onClick={() =>
              setLignes((p) => [
                ...p,
                {
                  label: '',
                  description: undefined,
                  quantity: 1,
                  unitPrice: 0,
                  discountRate: 0,
                  taxRate: 0,
                },
              ])
            }
          >
            Ajouter une ligne
          </Button>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Nouveau total TTC
          </Typography>
          <Typography className="tabular" variant="h3" sx={{ fontWeight: 700 }}>
            {prix(nouveauTotal)}
          </Typography>
        </Box>

        {/* ⚠️ On montre l'effet sur le RÈGLEMENT, qui est l'angle mort de
            l'opération : corriger les lignes d'une facture déjà réglée crée
            mécaniquement un trop-perçu ou un reste dû, et personne n'y pense
            au moment de corriger un libellé. */}
        {vente.paidAmount > 0 && ecartReglement !== 0 && (
          <Alert severity={ecartReglement > 0 ? 'warning' : 'info'}>
            {ecartReglement > 0
              ? `Le client a réglé ${prix(vente.paidAmount)} : il devient créditeur de ${prix(ecartReglement)}. Émettez un avoir ou remboursez.`
              : `Le client a réglé ${prix(vente.paidAmount)} : il restera dû ${prix(-ecartReglement)}.`}
          </Alert>
        )}

        {!brouillon && (
          <TextField
            label="Motif de la modification"
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            placeholder="Erreur de quantité constatée au comptoir"
            helperText="Obligatoire : il apparaîtra dans l’historique du document."
            multiline
            minRows={2}
            fullWidth
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 6, py: 4 }}>
        <Button onClick={onClose}>Annuler</Button>
        <Button
          variant="contained"
          disabled={!valide || enCours}
          onClick={async () => {
            setEnCours(true)
            try {
              await onValider(lignes, motif.trim())
            } finally {
              setEnCours(false)
            }
          }}
        >
          {enCours ? 'Enregistrement…' : 'Enregistrer la modification'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
